import { Observable } from 'rxjs';
import { CropWorkType } from '../enums/crop-work-types';
import ClassifierValue from '../interfaces/classifierValue';
import { CropWorkType as iCropWorkType } from '../interfaces/crop-work-type';
import Employee from '../interfaces/employee';
import Field from '../interfaces/field';
import Paginate from '../interfaces/paginate';
import Work from '../interfaces/work';
import { YieldAccounting } from '../interfaces/yieldAccounting';
import { HttpClient, ListParams } from './http-client';
import { CropGroupValues } from '../enums/crop-group-values';

export class WorkService extends HttpClient {
  index(
    farmId: string,
    season: string,
    params: ListParams
  ): Observable<Paginate<Work>> {
    return this.get<Paginate<Work>>(
      `/api/farms/${farmId}/season/${season}/works`,
      params
    );
  }

  worksInPeriod(
    farmId: string,
    season: string,
    from: string,
    to: string
  ): Observable<Work[]> {
    return this.get<Work[]>(
      `/api/farms/${farmId}/season/${season}/works/in-period`,
      { from, to }
    );
  }

  show(farmId: string, id: string): Observable<Work> {
    return this.get<Work>(`/api/farms/${farmId}/works/${id}`);
  }

  yieldAccounting(farmId: string, season: string, params: ListParams) {
    return this.get<Paginate<YieldAccounting>>(
      `/api/farms/${farmId}/season/${season}/works/yield-accounting`,
      params
    );
  }

  store(farmId: string, work: Work): Observable<Work> {
    return this.post<Work>(`/api/farms/${farmId}/works`, work);
  }

  update(farmId: string, id: string, work: Work): Observable<Work> {
    return this.put<Work>(`/api/farms/${farmId}/works/${id}`, work);
  }

  destroy(farmId: string, id: string): Observable<Work> {
    return this.delete<Work>(`/api/farms/${farmId}/works/${id}`);
  }

  setDone(farmId: string, ids: string[]): Observable<any> {
    return this.put<string[]>(`/api/farms/${farmId}/works/set-done`, {
      ids: ids,
    });
  }

  usedFields(
    farmId: string,
    season: string,
    params: ListParams = {}
  ): Observable<Field[]> {
    return this.get<Field[]>(
      `/api/farms/${farmId}/season/${season}/works/used-fields`,
      params
    );
  }

  usedYieldAccountingProductTypes(
    farmId: string,
    season: string,
    workType: string
  ) {
    return this.get<ClassifierValue[]>(
      `/api/farms/${farmId}/season/${season}/works/yield-accounting/${workType}/used-product-types`
    );
  }

  usedEmployees(farmId: string, season: string): Observable<Employee[]> {
    return this.get<Employee[]>(
      `/api/farms/${farmId}/season/${season}/works/used-employees`
    );
  }

  usedCropWorkTypes(
    farmId: string,
    season: string
  ): Observable<iCropWorkType[]> {
    return this.get<iCropWorkType[]>(
      `/api/farms/${farmId}/season/${season}/works/used-crop-work-types`
    );
  }

  usedWorks(farmId: string, season: string): Observable<ClassifierValue[]> {
    return this.get<ClassifierValue[]>(
      `/api/farms/${farmId}/season/${season}/works/used-works`
    );
  }

  usedFertilizerProductTypes(
    farmId: string,
    season: string,
    params?: {
      [key: string]: string;
    }
  ) {
    return this.get<Paginate<ClassifierValue>>(
      `/api/farms/${farmId}/season/${season}/works/used-fertilizer-product-types`,
      params
    );
  }

  exportYieldXls(farmId: string, season: string, workType: string) {
    return this.export(
      `/api/farms/${farmId}/season/${season}/works/yield-accounting/${workType}`
    );
  }

  workTypeIsAllowedForFields(workType: CropWorkType, fields: Field[]): boolean {
    const hasInactiveField = fields.filter((field) => !field.active).length > 0;

    const hasFieldWithoutCrop =
      fields.filter(
        (field) => !field.crop_id || !field.crop_group_id || !field.breed
      ).length > 0;

    const fieldsHasSameCropGroup =
      fields
        .map((field) => field.crop_group_id)
        .reduce((distinctIds, id) => {
          if (!distinctIds.includes(id)) distinctIds.push(id);
          return distinctIds;
        }, []).length === 1;

    switch (true) {
      case hasInactiveField:
        return false;
      // break;
      case fields.length === 0:
      case workType === CropWorkType.SOIL_TREATMENT:
      case workType === CropWorkType.SOWING_OR_PLANTING:
      case workType === CropWorkType.DISPERSION_OR_SPRAYING:
      case workType === CropWorkType.MOWING:
      case workType === CropWorkType.OTHER:
        return true;
      // break;
      case workType === CropWorkType.THRESHING:
        return (
          !hasFieldWithoutCrop &&
          this.hasNoInvalidCropGroup(
            fields,
            this.forbiddenCropGroupsForWorkType(CropWorkType.THRESHING)
          )
        );
      // break;
      case workType === CropWorkType.FORAGE_HARVESTING:
        return !hasFieldWithoutCrop;
      // break;
      case workType === CropWorkType.FRUIT_HARVESTING:
        return (
          !hasFieldWithoutCrop &&
          this.hasNoInvalidCropGroup(
            fields,
            this.forbiddenCropGroupsForWorkType(CropWorkType.FRUIT_HARVESTING)
          ) &&
          fieldsHasSameCropGroup
        );
      // break;
      case workType === CropWorkType.VEGETABLE_ETC_HARVESTING:
        return (
          !hasFieldWithoutCrop &&
          this.hasNoInvalidCropGroup(
            fields,
            this.forbiddenCropGroupsForWorkType(
              CropWorkType.VEGETABLE_ETC_HARVESTING
            )
          ) &&
          fieldsHasSameCropGroup
        );
      // break;
    }
  }

  workTypeExpectsFieldHasCrop(workType: CropWorkType): boolean {
    switch (workType) {
      case CropWorkType.THRESHING:
      case CropWorkType.FORAGE_HARVESTING:
      case CropWorkType.FRUIT_HARVESTING:
      case CropWorkType.VEGETABLE_ETC_HARVESTING:
        return true;
      default:
        return false;
    }
  }

  hasNoInvalidCropGroup(fields: Field[], invalidCropGroups: string[]): boolean {
    return fields
      .map((field) => field.crop_group_value)
      .every((cropGroup) => invalidCropGroups.indexOf(cropGroup) === -1);
  }

  allowedWorkTypesForFields(fields: Field[]): CropWorkType[] {
    const allowedWorkTypes: CropWorkType[] = [];

    Object.entries(CropWorkType).forEach(([key, workType]) => {
      if (this.workTypeIsAllowedForFields(workType, fields)) {
        allowedWorkTypes.push(workType);
      }
    });

    return allowedWorkTypes;
  }

  forbiddenCropGroupsForWorkType(workType: CropWorkType): string[] {
    switch (workType) {
      case CropWorkType.THRESHING:
        return [
          CropGroupValues.VEGETABLES,
          CropGroupValues.FRUITS_AND_BERRIES_1_TO_3_YEARS,
          CropGroupValues.FRUITS_AND_BERRIES_4_AND_MORE_YEARS,
        ];
      case CropWorkType.FRUIT_HARVESTING:
        return [CropGroupValues.VEGETABLES, CropGroupValues.CEREALS];
      case CropWorkType.VEGETABLE_ETC_HARVESTING:
        return [
          CropGroupValues.FRUITS_AND_BERRIES_1_TO_3_YEARS,
          CropGroupValues.FRUITS_AND_BERRIES_4_AND_MORE_YEARS,
          CropGroupValues.CEREALS,
        ];
      default:
        return [];
    }
  }

  exportWorkOverviewXls(farmId: string, season: string) {
    return this.export(`/api/farms/${farmId}/season/${season}/works/excel`);
  }
}
